import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTeam } from '../context/TeamContext';
import { useToast } from '../context/ToastContext';
import Navigation from '../components/Navigation';
import ActionMenu from '../components/ActionMenu';
import ConfirmAvailabilityModal from '../components/ConfirmAvailabilityModal';
import MatchCard from '../components/MatchCard';
import axios from 'axios';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import { Calendar as CalendarIcon, List, Eye, Users, Calendar, CheckCircle, XCircle, AlertCircle, HelpCircle, Filter, ChevronDown, Clock, Check, X } from 'lucide-react';
import { formatMatchDateFull } from '../utils/dateFormatting';
import { MatchTypeIcon } from '../components/icons/MatchTypeIcon';
import DifficultyGauge from '../components/DifficultyGauge';
import { confirmLineupPosition, unconfirmLineupPosition } from '../services/lineupConfirmationService';
import { getDisplayName } from '../utils/displayName';

interface Match {
  id: number;
  opponent_name: string;
  match_date: string;
  location?: string;
  formatted_address?: string;
  latitude?: number;
  longitude?: number;
  match_type?: string;
  home_away?: string;
  notes?: string;
  lineup_published?: boolean;
  team_id: number;
  team?: {
    id: number;
    name: string;
  };
}

interface PlayerAvailability {
  id: number;
  match_id: number;
  player_id: number;
  availability_status: 'available' | 'unavailable' | 'maybe';
  notes?: string;
  updated_at: string;
}

interface LineupPlayer {
  id: number;
  name: string;
  display_name?: string;
  tennisrecord_name?: string | null;
  email: string;
}

interface LineupConfirmation {
  id: number;
  lineup_id: number;
  player_id: number;
  confirmed_at: string;
  confirmed_by: number;
}

interface LineupDifficulty {
  team_avg_rating: number;
  opponent_avg_rating: number;
  rating_diff: number;
  category: 'much-easier' | 'easier' | 'even' | 'harder' | 'much-harder';
}

interface LineupPosition {
  id: number;
  match_id: number;
  position_number: number;
  player1_id: number;
  player2_id?: number | null;
  player1?: LineupPlayer;
  player2?: LineupPlayer | null;
  score?: string | null;
  won?: boolean | null;
  is_actual_result?: boolean;
  opponent_player1_name?: string | null;
  opponent_player2_name?: string | null;
  confirmations?: LineupConfirmation[] | null;
  difficulty?: LineupDifficulty | null;
}

const MatchesPage: React.FC = () => {
  const { user } = useAuth();
  const { teams, loading: teamLoading } = useTeam();
  const toast = useToast();
  const navigate = useNavigate();
  const [allMatches, setAllMatches] = useState<Match[]>([]);
  const [matches, setMatches] = useState<Match[]>([]);
  const [loading, setLoading] = useState(true);
  const [availability, setAvailability] = useState<Map<number, PlayerAvailability>>(new Map());
  const [matchLineups, setMatchLineups] = useState<{ [matchId: number]: LineupPosition[] }>({});
  const [modalLineups, setModalLineups] = useState<{ [matchId: number]: LineupPosition[] }>({});
  const [teamLineupFormats, setTeamLineupFormats] = useState<{ [teamId: number]: any }>({});
  const [updating, setUpdating] = useState<number | null>(null);
  
  // Helper function to detect if device is mobile (width < 640px, Tailwind's 'sm' breakpoint)
  const isMobileDevice = (): boolean => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth < 640;
  };
  
  // View mode and filter - default to list view on mobile, calendar view on desktop
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingConfirmation, setPendingConfirmation] = useState<{ matchId: number; lineupId: number; playerId: number } | null>(null);
  const [viewMode, setViewMode] = useState<'calendar' | 'list'>(() => 
    isMobileDevice() ? 'list' : 'calendar'
  );
  const [matchFilter, setMatchFilter] = useState<'all' | 'upcoming' | 'past'>('upcoming');
  const [selectedTeamFilter, setSelectedTeamFilter] = useState<number | 'all'>('all');
  const [selectedDayMatches, setSelectedDayMatches] = useState<Match[]>([]);
  const [showDayModal, setShowDayModal] = useState(false);
  const [mobileFiltersExpanded, setMobileFiltersExpanded] = useState(false);
  const [windowWidth, setWindowWidth] = useState(() => 
    typeof window !== 'undefined' ? window.innerWidth : 1024
  );

  // Track window resize for responsive calendar sizing
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Calculate calendar height for desktop to fit on single page
  const calendarHeight = useMemo(() => {
    if (windowWidth < 1024) {
      return 'auto'; // Mobile: keep auto
    }
    // Desktop: calculate based on viewport
    // Account for: nav (~64px), header/filters (~150px), legend (~30px), padding (~5px)
    return 'calc(100vh - 250px)';
  }, [windowWidth]);

  // Calculate responsive dayMaxEvents
  const dayMaxEventsValue = useMemo(() => {
    return windowWidth >= 1024 ? 2 : 4;
  }, [windowWidth]);

  useEffect(() => {
    const fetchMatches = async () => {
      if (!user) {
        setAllMatches([]);
        setMatches([]);
        setAvailability(new Map());
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        
        // Fetch matches and availability in parallel
        const [matchesResponse, availabilityResponse] = await Promise.all([
          axios.get(`/matches/users/${user.id}/matches`),
          axios.get(`/availability/users/${user.id}/availability`)
        ]);
        
        const matchesData = matchesResponse.data;
        const availabilityData = availabilityResponse.data;
        
        // Sort matches by date (upcoming first)
        matchesData.sort((a: Match, b: Match) => 
          new Date(a.match_date).getTime() - new Date(b.match_date).getTime()
        );
        
        setAllMatches(matchesData);
        
        // Create availability map for quick lookup
        const availabilityMap = new Map<number, PlayerAvailability>();
        availabilityData.forEach((avail: PlayerAvailability) => {
          availabilityMap.set(avail.match_id, avail);
        });
        setAvailability(availabilityMap);
        
        // Fetch lineup data for all matches
        const lineupPromises = matchesData.map((match: Match) =>
          axios.get(`/lineups/matches/${match.id}/lineup`)
            .then(response => ({ matchId: match.id, lineup: response.data }))
            .catch(() => ({ matchId: match.id, lineup: [] }))
        );
        
        const lineupResults = await Promise.all(lineupPromises);
        const lineupsMap: { [matchId: number]: LineupPosition[] } = {};
        lineupResults.forEach(result => {
          lineupsMap[result.matchId] = result.lineup;
        });
        setMatchLineups(lineupsMap);
      } catch (error) {
        console.error('Failed to fetch matches:', error);
        toast.error('Failed to load matches. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    if (!teamLoading && user) {
      fetchMatches();
    }
  }, [teamLoading, user, toast, teams]);

  // Filter matches by team
  useEffect(() => {
    let filtered = allMatches;
    
    if (selectedTeamFilter !== 'all') {
      filtered = filtered.filter(match => match.team_id === selectedTeamFilter);
    }
    
    setMatches(filtered);
  }, [allMatches, selectedTeamFilter]);

  // Helper function to check if a match is in the past
  const isMatchInPast = (matchDate: string): boolean => {
    return new Date(matchDate) < new Date();
  };

  // Get availability status for a match
  const getAvailabilityStatus = (matchId: number): 'available' | 'unavailable' | 'maybe' | null => {
    const avail = availability.get(matchId);
    return avail ? avail.availability_status : null;
  };

  // Get availability indicator color
  const getAvailabilityColor = (status: 'available' | 'unavailable' | 'maybe' | null): string => {
    if (status === 'available') return '#10b981'; // green
    if (status === 'unavailable') return '#ef4444'; // red
    if (status === 'maybe') return '#f59e0b'; // yellow
    return '#6b7280'; // gray for not set
  };

  // Get availability status text
  const getAvailabilityText = (status: 'available' | 'unavailable' | 'maybe' | null): string => {
    if (status === 'available') return 'Available';
    if (status === 'unavailable') return 'Unavailable';
    if (status === 'maybe') return 'If Needed';
    return 'Not Set';
  };


  // Check if a player has confirmed their lineup position
  const isPlayerConfirmed = (lineupPosition: LineupPosition, playerId: number): boolean => {
    if (!lineupPosition.confirmations) return false;
    return lineupPosition.confirmations.some(conf => conf.player_id === playerId);
  };

  // Get confirmation status indicator for a player (compact visual)
  const getConfirmationStatus = (lineupPosition: LineupPosition, playerId: number) => {
    const isConfirmed = isPlayerConfirmed(lineupPosition, playerId);
    if (isConfirmed) {
      return (
        <span title="Confirmed" className="inline-flex items-center ml-1.5">
          <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
        </span>
      );
    }
    return (
      <span title="Not confirmed" className="inline-flex items-center ml-1.5">
        <div className="w-2 h-2 rounded-full bg-gray-400 dark:bg-gray-500 flex-shrink-0"></div>
      </span>
    );
  };

  // Handle confirmation in modal
  const handleConfirmInModal = async (matchId: number, lineupId: number, playerId: number) => {
    if (!user) return;
    
    const availabilityStatus = getAvailabilityStatus(matchId);
    const shouldSetAvailable = availabilityStatus !== 'available';
    
    // If user is not available, show modal to confirm and set as available
    if (shouldSetAvailable) {
      setPendingConfirmation({ matchId, lineupId, playerId });
      setShowConfirmModal(true);
      return;
    }
    
    // If already available, proceed with confirmation
    await performConfirmationInModal(matchId, lineupId, playerId, false);
  };

  // Perform the actual confirmation in modal
  const performConfirmationInModal = async (matchId: number, lineupId: number, playerId: number, setAvailable: boolean) => {
    if (!user) return;
    
    try {
      // Confirm lineup position
      await confirmLineupPosition(matchId, lineupId, playerId);
      
      // If user is not available, also set availability to available
      if (setAvailable) {
        try {
          await axios.post(`/availability/matches/${matchId}/availability`, {
            match_id: matchId,
            player_id: user.id,
            availability_status: 'available',
            notes: ''
          });
          
          // Refresh availability data
          const availabilityResponse = await axios.get(`/availability/matches/${matchId}/availability`);
          const availabilityArray = availabilityResponse.data;
          const availabilityMap = new Map<number, PlayerAvailability>();
          availabilityArray.forEach((avail: PlayerAvailability) => {
            availabilityMap.set(avail.match_id, avail);
          });
          setAvailability(availabilityMap);
        } catch (error) {
          console.error('Failed to set availability:', error);
          // Continue even if availability update fails - lineup is already confirmed
        }
      }
      
      toast.success('Lineup position confirmed' + (setAvailable ? ' and availability set to Available' : ''));
      
      // Refresh lineup data for this match
      const response = await axios.get(`/lineups/matches/${matchId}/lineup`);
      setModalLineups(prev => ({
        ...prev,
        [matchId]: response.data
      }));
    } catch (error: any) {
      toast.error(error.message || 'Failed to confirm lineup position');
    }
  };

  // Handle modal confirm
  const handleModalConfirm = () => {
    if (pendingConfirmation) {
      performConfirmationInModal(
        pendingConfirmation.matchId,
        pendingConfirmation.lineupId,
        pendingConfirmation.playerId,
        true
      );
      setShowConfirmModal(false);
      setPendingConfirmation(null);
    }
  };

  // Handle modal cancel
  const handleModalCancel = () => {
    setShowConfirmModal(false);
    setPendingConfirmation(null);
  };

  // Handle unconfirmation in modal
  const handleUnconfirmInModal = async (matchId: number, lineupId: number, playerId: number) => {
    if (!user) return;
    try {
      await unconfirmLineupPosition(matchId, lineupId, playerId);
      toast.success('Lineup position unconfirmed');
      // Refresh lineup data for this match
      const response = await axios.get(`/lineups/matches/${matchId}/lineup`);
      setModalLineups(prev => ({
        ...prev,
        [matchId]: response.data
      }));
    } catch (error: any) {
      toast.error(error.message || 'Failed to unconfirm lineup position');
    }
  };

  // Check if user can confirm/unconfirm a position (player for their own, captain for any)
  const canConfirmPositionInModal = (playerId: number, match: Match): boolean => {
    if (!user) return false;
    // For now, allow if user is the player themselves
    // TODO: Add proper captain check if team members are available
    return user.id === playerId;
  };

  // Check if user is in lineup for a match
  const isUserInLineup = useCallback((matchId: number): boolean => {
    if (!user || !matchLineups[matchId]) return false;
    
    const match = matches.find(m => m.id === matchId);
    if (!match) return false;
    
    // Check if lineup is visible (published or match is in past)
    const matchDate = new Date(match.match_date);
    const isPast = matchDate < new Date();
    const isPublished = match.lineup_published === true;
    
    // Lineup indicators only show if lineup is published (or match is in past)
    if (!isPast && !isPublished) return false;
    
    const lineup = matchLineups[matchId];
    return lineup.some(
      position => position.player1_id === user.id || position.player2_id === user.id
    );
  }, [user, matchLineups, matches]);

  // Check if user has confirmed their lineup position for a match
  const hasUserConfirmedLineup = useCallback((matchId: number): boolean => {
    if (!user || !matchLineups[matchId]) return false;
    
    const match = matches.find(m => m.id === matchId);
    if (!match) return false;
    
    // Check if lineup is visible (published or match is in past)
    const matchDate = new Date(match.match_date);
    const isPast = matchDate < new Date();
    const isPublished = match.lineup_published === true;
    
    // Lineup indicators only show if lineup is published (or match is in past)
    if (!isPast && !isPublished) return false;
    
    const lineup = matchLineups[matchId];
    return lineup.some(position => {
      if (position.player1_id === user.id) {
        return isPlayerConfirmed(position, user.id);
      }
      if (position.player2_id === user.id) {
        return isPlayerConfirmed(position, user.id);
      }
      return false;
    });
  }, [user, matchLineups, matches]);

  // Helper function to check if a match type is singles
  const isSinglesPosition = (matchType: string): boolean => {
    if (!matchType) return false;
    const lowerType = matchType.toLowerCase();
    return lowerType.includes('singles') && !lowerType.includes('doubles');
  };

  // Helper function to format match type for display
  const formatMatchType = (matchType: string): string => {
    if (!matchType) return '';
    return matchType
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Get availability badge classes
  const getStatusBadgeClass = (status: 'available' | 'unavailable' | 'maybe' | null): string => {
    if (status === 'available') return 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 border-green-300 dark:border-green-700';
    if (status === 'maybe') return 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300 border-yellow-300 dark:border-yellow-700';
    if (status === 'unavailable') return 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300 border-red-300 dark:border-red-700';
    return 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300 border-gray-300 dark:border-gray-600';
  };

  // Handle availability change for a single match
  const handleAvailabilityChange = async (
    matchId: number,
    status: 'available' | 'unavailable' | 'maybe'
  ) => {
    if (!user) return;
    
    const currentStatus = getAvailabilityStatus(matchId);
    
    // If clicking the same status, toggle it off (unset)
    if (currentStatus === status) {
      await handleClearAvailability(matchId);
      return;
    }
    
    // Optimistically update the state immediately
    const optimisticAvailability: PlayerAvailability = {
      id: availability.get(matchId)?.id || 0,
      match_id: matchId,
      player_id: user.id,
      availability_status: status,
      notes: '',
      updated_at: new Date().toISOString()
    };
    
    setAvailability(prev => {
      const newMap = new Map(prev);
      newMap.set(matchId, optimisticAvailability);
      return newMap;
    });
    
    try {
      setUpdating(matchId);
      
      // Use the response from POST directly instead of refetching
      const response = await axios.post(`/availability/matches/${matchId}/availability`, {
        match_id: matchId,
        player_id: user.id,
        availability_status: status,
        notes: ''
      });
      
      // Update with the actual response from the server
      const updatedAvailability = response.data;
      setAvailability(prev => {
        const newMap = new Map(prev);
        newMap.set(matchId, updatedAvailability);
        return newMap;
      });
      
      toast.success('Availability updated successfully!');
    } catch (error) {
      console.error('Failed to set availability:', error);
      
      // Revert optimistic update on error
      const original = availability.get(matchId);
      setAvailability(prev => {
        const newMap = new Map(prev);
        if (original) {
          newMap.set(matchId, original);
        } else {
          newMap.delete(matchId);
        }
        return newMap;
      });
      
      
      toast.error('Failed to update availability. Please try again.');
    } finally {
      setUpdating(null);
    }
  };

  // Handle clearing availability for a match
  const handleClearAvailability = async (matchId: number) => {
    if (!user) return;
    
    const match = matches.find(m => m.id === matchId);
    if (!match) return;
    
    // Store original value for potential rollback
    const originalAvailability = availability.get(matchId);
    
    // Optimistically remove from availability map immediately
    setAvailability(prev => {
      const newMap = new Map(prev);
      newMap.delete(matchId);
      return newMap;
    });
    
    
    try {
      setUpdating(matchId);
      
      await axios.delete(`/teams/${match.team_id}/availability/${matchId}/${user.id}`);
      
      toast.success('Availability cleared successfully!');
    } catch (error) {
      console.error('Failed to clear availability:', error);
      
      // Revert optimistic update on error
      if (originalAvailability) {
        setAvailability(prev => {
          const newMap = new Map(prev);
          newMap.set(matchId, originalAvailability);
          return newMap;
        });
        
      }
      
      toast.error('Failed to clear availability. Please try again.');
    } finally {
      setUpdating(null);
    }
  };

  // Apply filters to matches - only for list view
  const filteredMatches = useMemo(() => {
    if (viewMode === 'calendar') {
      // Calendar view always shows all matches
      return matches;
    }
    // List view applies the filter
    return matches.filter(match => {
      if (matchFilter === 'upcoming') return !isMatchInPast(match.match_date);
      if (matchFilter === 'past') return isMatchInPast(match.match_date);
      return true; // 'all'
    });
  }, [matches, matchFilter, viewMode]);


  // Convert matches to FullCalendar events
  const calendarEvents = useMemo(() => {
    return matches.map(match => {
      const matchDate = new Date(match.match_date);
      const isPast = isMatchInPast(match.match_date);
      const userInLineup = isUserInLineup(match.id);
      const timeStr = matchDate.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit'
      });
      
      // Shorter title - just time and opponent
      const prefix = match.home_away === 'away' ? 'at' : 'vs';
      let title = `${timeStr} ${prefix} ${match.opponent_name}`;
      
      // Determine colors based on match status
      let backgroundColor: string;
      let borderColor: string;
      
      if (isPast) {
        // Past matches: gray
        backgroundColor = '#6b7280';
        borderColor = '#4b5563';
      } else if (userInLineup) {
        // Upcoming matches where user is in lineup: green
        backgroundColor = '#10b981';
        borderColor = '#059669';
      } else {
        // Upcoming matches where user is NOT in lineup: blue
        backgroundColor = '#3b82f6';
        borderColor = '#2563eb';
      }
      
      return {
        id: match.id.toString(),
        title: title,
        start: matchDate.toISOString(),
        allDay: false,
        backgroundColor: backgroundColor,
        borderColor: borderColor,
        textColor: '#ffffff',
        extendedProps: {
          match: match,
          location: match.location || '',
          homeAway: match.home_away || '',
          teamName: match.team?.name || '',
          userInLineup: userInLineup
        }
      };
    });
  }, [matches, isUserInLineup]);

  // Handle calendar date change (month navigation)
  const handleDatesSet = (arg: any) => {
    // No-op: previously updated selectedDate, but it's no longer needed
  };

  // Handle calendar event click
  const handleEventClick = async (clickInfo: any) => {
    const match = clickInfo.event.extendedProps.match;
    setSelectedDayMatches([match]);
    setShowDayModal(true);
    
    // Fetch full lineup data with player info and availability for the clicked match
    try {
      const [lineupResponse, formatResponse] = await Promise.all([
        axios.get(`/lineups/matches/${match.id}/lineup`),
        teamLineupFormats[match.team_id] 
          ? Promise.resolve({ data: teamLineupFormats[match.team_id] })
          : axios.get(`/teams/${match.team_id}/scheduling`).then(res => ({ data: res.data.lineup_format || null })).catch(() => ({ data: null }))
      ]);
      
      setModalLineups(prev => ({
        ...prev,
        [match.id]: lineupResponse.data || []
      }));
      
      
      if (formatResponse.data && !teamLineupFormats[match.team_id]) {
        setTeamLineupFormats(prev => ({
          ...prev,
          [match.team_id]: formatResponse.data
        }));
      }
    } catch (error) {
      console.error('Failed to fetch lineup:', error);
      setModalLineups(prev => ({
        ...prev,
        [match.id]: []
      }));
    }
  };


  // Disable body scrolling when modal is open
  useEffect(() => {
    if (showDayModal) {
      // Store the current scroll position
      const scrollY = window.scrollY;
      // Disable scrolling on body and html
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
    } else {
      // Restore scrolling
      const scrollY = document.body.style.top;
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
      // Restore scroll position
      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY || '0') * -1);
      }
    }
    
    // Cleanup on unmount
    return () => {
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    };
  }, [showDayModal]);

  // Match card skeleton component for list view
  const MatchCardSkeleton = () => (
    <div className="bg-white dark:bg-gray-800 rounded-xl p-4 md:p-6 shadow-sm border border-gray-100 dark:border-gray-700/50 animate-pulse">
      <div className="flex items-center justify-between gap-4">
        <div className="flex-1 min-w-0 pr-3">
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 mb-2">
            <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-48"></div>
            <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded-full w-32"></div>
          </div>
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-32 mb-2"></div>
          <div className="space-y-2">
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-64"></div>
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-56"></div>
          </div>
        </div>
        <div className="w-4 h-4 bg-gray-200 dark:bg-gray-700 rounded"></div>
      </div>
    </div>
  );

  // Calendar skeleton component
  const CalendarSkeleton = () => (
    <div className="bg-white dark:bg-gray-800 rounded-xl p-3 md:p-6 shadow-sm border border-gray-100 dark:border-gray-700/50 animate-pulse">
      {/* Calendar header skeleton */}
      <div className="mb-4 flex items-center justify-between">
        <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-48"></div>
        <div className="flex gap-2">
          <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded w-20"></div>
          <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded w-20"></div>
          <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded w-20"></div>
        </div>
      </div>
      
      {/* Calendar grid skeleton */}
      <div className="space-y-2">
        {/* Weekday headers */}
        <div className="grid grid-cols-7 gap-1 mb-2">
          {[...Array(7)].map((_, i) => (
            <div key={i} className="h-8 bg-gray-200 dark:bg-gray-700 rounded"></div>
          ))}
        </div>
        
        {/* Calendar days grid */}
        <div className="grid grid-cols-7 gap-1">
          {[...Array(35)].map((_, i) => (
            <div key={i} className="aspect-square bg-gray-100 dark:bg-gray-700/50 rounded border border-gray-200 dark:border-gray-600 p-2">
              <div className="h-4 bg-gray-200 dark:bg-gray-600 rounded w-6 mb-1"></div>
              <div className="space-y-1">
                <div className="h-3 bg-gray-200 dark:bg-gray-600 rounded w-full"></div>
                <div className="h-3 bg-gray-200 dark:bg-gray-600 rounded w-3/4"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  if (teamLoading) {
    return (
      <div className="min-h-screen">
        <Navigation />
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 dark:border-primary-400"></div>
        </div>
      </div>
    );
  }


  return (
    <div className="min-h-screen">
      <Navigation />

      {/* Main Content */}
      <div className="max-w-7xl mx-auto pt-6 pb-1 px-4 sm:px-6 lg:px-8">
        <div>
          {/* Header and Filters */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4 md:mb-6">
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-gray-100 tracking-tight">My Matches</h1>
            <div className="flex flex-col gap-2">
              {/* Top Row: View Toggle and Filter Toggle */}
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-end gap-2">
                {/* View Toggle - Always visible, appears first on mobile */}
                <div className="flex items-center gap-1 bg-gray-100 dark:bg-gray-800 rounded-lg p-1 w-full sm:w-auto order-1 sm:order-3">
                  <button
                    onClick={() => setViewMode('calendar')}
                    className={`flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors flex-1 sm:flex-initial min-h-[44px] ${
                      viewMode === 'calendar'
                        ? 'bg-white dark:bg-gray-700 text-primary-600 dark:text-primary-400 shadow-sm'
                        : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100'
                    }`}
                  >
                    <CalendarIcon className="w-4 h-4" />
                    <span>Calendar</span>
                  </button>
                  <button
                    onClick={() => setViewMode('list')}
                    className={`flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors flex-1 sm:flex-initial min-h-[44px] ${
                      viewMode === 'list'
                        ? 'bg-white dark:bg-gray-700 text-primary-600 dark:text-primary-400 shadow-sm'
                        : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100'
                    }`}
                  >
                    <List className="w-4 h-4" />
                    List
                  </button>
                </div>
                
                {/* Mobile Toggle Button - Only visible on mobile, appears second on mobile */}
                <button
                  onClick={() => setMobileFiltersExpanded(!mobileFiltersExpanded)}
                  className="sm:hidden flex items-center gap-2 px-3 py-2.5 border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-lg shadow-sm text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 min-h-[44px] order-2 sm:order-1"
                >
                  <Filter className="w-4 h-4" />
                  <span>{mobileFiltersExpanded ? 'Hide' : 'Show'} Filters</span>
                  <ChevronDown className={`w-4 h-4 transition-transform ${mobileFiltersExpanded ? 'transform rotate-180' : ''}`} />
                </button>
                
                {/* Filters Container - Collapsible on mobile, appears third on mobile */}
                <div className={`flex-wrap items-center gap-2 w-full sm:w-auto ${mobileFiltersExpanded ? 'flex' : 'hidden sm:flex'} order-3 sm:order-2`}>
                  {teams.length > 0 && (
                    <select
                      id="team-filter"
                      value={selectedTeamFilter}
                      onChange={(e) => setSelectedTeamFilter(e.target.value === 'all' ? 'all' : parseInt(e.target.value))}
                      className="w-full sm:w-auto px-3 py-2.5 border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 min-h-[44px]"
                    >
                      <option value="all">All Teams</option>
                      {teams.map(team => (
                        <option key={team.id} value={team.id}>{team.name}</option>
                      ))}
                    </select>
                  )}
                  {/* Match Filter - Only show in list view */}
                  {viewMode === 'list' && (
                    <>
                      <label htmlFor="match-filter" className="text-sm text-gray-600 dark:text-gray-400 hidden sm:block">Show:</label>
                      <select
                        id="match-filter"
                        value={matchFilter}
                        onChange={(e) => setMatchFilter(e.target.value as 'all' | 'upcoming' | 'past')}
                        className="w-full sm:w-auto px-3 py-2.5 border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 min-w-[180px] min-h-[44px]"
                      >
                        <option value="all">All Matches</option>
                        <option value="upcoming">Upcoming Matches</option>
                        <option value="past">Past Matches</option>
                      </select>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Results Summary - Below all filters, only show in list view, always visible */}
          {viewMode === 'list' && (
            <div className="mb-4">
              <p className="text-xs md:text-sm text-gray-500 dark:text-gray-400">
                Showing {filteredMatches.length} of {matches.length} matches
              </p>
            </div>
          )}

          {/* Calendar View */}
          {viewMode === 'calendar' && (
            <div className="mb-1">
              {loading ? (
                <CalendarSkeleton />
              ) : (
                <div className="bg-white dark:bg-gray-800 rounded-xl px-3 pt-2 pb-0.5 md:px-6 md:pt-3 md:pb-1 shadow-sm border border-gray-100 dark:border-gray-700/50 overflow-hidden">
                <style>{`
                  .fc {
                    font-family: inherit;
                  }
                  .fc-header-toolbar {
                    margin-bottom: 0;
                    flex-direction: column;
                    gap: 0.75em;
                    padding: 0.5em 0;
                  }
                  @media (min-width: 640px) {
                    .fc-header-toolbar {
                      flex-direction: row;
                    }
                  }
                  /* Mobile: Better spacing for stacked layout */
                  @media (max-width: 639px) {
                    .fc-header-toolbar {
                      gap: 0.6em;
                      margin-bottom: 0;
                      padding: 0.4em 0;
                    }
                    .fc-toolbar-chunk {
                      justify-content: center;
                      width: 100%;
                      gap: 0.5em;
                    }
                    .fc-toolbar-chunk.fc-toolbar-chunk-center {
                      gap: 0.5em;
                      flex-wrap: nowrap;
                    }
                    .fc-toolbar-chunk.fc-toolbar-chunk-center .fc-prev-button {
                      margin-right: 0.3em;
                    }
                    .fc-toolbar-chunk.fc-toolbar-chunk-center .fc-toolbar-title {
                      margin: 0 0.5em 0 0;
                      flex-shrink: 0;
                    }
                    .fc-toolbar-chunk.fc-toolbar-chunk-center .fc-next-button {
                      margin-left: 0.3em;
                    }
                    .fc-toolbar-chunk.fc-toolbar-chunk-right {
                      margin-top: 0;
                    }
                    .fc-today-button {
                      display: none !important;
                    }
                    .fc-toolbar-chunk.fc-toolbar-chunk-right {
                      display: none !important;
                    }
                  }
                  @media (min-width: 1024px) {
                    .fc-header-toolbar {
                      margin-bottom: 0;
                      padding: 0.15em 0;
                      gap: 0.5em;
                    }
                  }
                  .fc-toolbar-chunk {
                    display: flex;
                    align-items: center;
                    gap: 0.5em;
                  }
                  @media (min-width: 1024px) {
                    .fc-toolbar-chunk {
                      gap: 0.25em;
                    }
                    .fc-toolbar-chunk.fc-toolbar-chunk-center {
                      flex: 1;
                      justify-content: center;
                      gap: 0.25em;
                    }
                    .fc-toolbar-chunk.fc-toolbar-chunk-center .fc-prev-button {
                      margin-right: 0.2em;
                    }
                    .fc-toolbar-chunk.fc-toolbar-chunk-center .fc-toolbar-title {
                      margin: 0 0.25em 0 0;
                    }
                    .fc-toolbar-chunk.fc-toolbar-chunk-center .fc-next-button {
                      margin-left: 0.2em;
                    }
                    .fc-toolbar-chunk.fc-toolbar-chunk-left,
                    .fc-toolbar-chunk.fc-toolbar-chunk-right {
                      flex: 0 0 auto;
                    }
                  }
                  .fc-toolbar-title {
                    font-size: 1rem;
                    font-weight: 600;
                    color: #111827;
                    white-space: nowrap;
                  }
                  .dark .fc-toolbar-title {
                    color: #f3f4f6;
                  }
                  /* Narrow mobile: smaller font to fit on one line */
                  @media (max-width: 374px) {
                    .fc-toolbar-title {
                      font-size: 0.85rem;
                    }
                  }
                  @media (min-width: 1024px) {
                    .fc-toolbar-title {
                      font-size: 0.75rem;
                      font-weight: 500;
                    }
                  }
                  .fc-button {
                    background-color: #3b82f6;
                    border-color: #3b82f6;
                    color: white;
                    padding: 0.4em 0.8em;
                    font-size: 0.75rem;
                    font-weight: 500;
                    border-radius: 0.5rem;
                    min-height: 36px;
                    min-width: 36px;
                    transition: all 0.2s ease;
                    box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
                  }
                  @media (min-width: 1024px) {
                    .fc-button {
                      padding: 0.15em 0.4em;
                      font-size: 0.6rem;
                      min-height: 24px;
                      min-width: 24px;
                      border-radius: 0.25rem;
                    }
                    .fc-button.fc-prev-button,
                    .fc-button.fc-next-button {
                      min-width: 24px;
                      padding: 0.15em 0.35em;
                    }
                  }
                  .fc-button:hover {
                    background-color: #2563eb;
                    border-color: #2563eb;
                    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
                    transform: translateY(-1px);
                  }
                  .fc-button-primary:not(:disabled):active,
                  .fc-button-primary:not(:disabled).fc-button-active {
                    background-color: #1d4ed8;
                    border-color: #1d4ed8;
                    box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
                    transform: translateY(0);
                  }
                  .fc-button:focus {
                    outline: none;
                    ring: 2px;
                    ring-offset: 2px;
                    ring-color: #3b82f6;
                  }
                  .fc-today-button {
                    background-color: #6b7280;
                    border-color: #6b7280;
                  }
                  .fc-today-button:hover {
                    background-color: #4b5563;
                    border-color: #4b5563;
                  }
                  .fc-daygrid-day {
                    min-height: 120px;
                    border-color: #e5e7eb;
                  }
                  .dark .fc-daygrid-day {
                    border-color: #4b5563;
                  }
                  @media (min-width: 1024px) {
                    .fc-daygrid-day {
                      min-height: 85px;
                    }
                  }
                  .fc-daygrid-day-number {
                    padding: 8px;
                    font-weight: 500;
                    font-size: 0.875rem;
                    color: #374151;
                  }
                  .dark .fc-daygrid-day-number {
                    color: #d1d5db;
                  }
                  @media (min-width: 1024px) {
                    .fc-daygrid-day-number {
                      padding: 4px 6px;
                      font-size: 0.8rem;
                    }
                  }
                  .fc-day-today {
                    background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 50%, #bbf7d0 100%) !important;
                  }
                  .dark .fc-day-today {
                    background: linear-gradient(135deg, #064e3b 0%, #065f46 50%, #047857 100%) !important;
                  }
                  .fc-day-today .fc-daygrid-day-number {
                    color: #166534;
                    font-weight: 600;
                  }
                  .dark .fc-day-today .fc-daygrid-day-number {
                    color: #6ee7b7;
                  }
                  .fc-event {
                    cursor: pointer;
                    border-radius: 0.375rem;
                    padding: 6px 8px;
                    margin: 3px 0;
                    font-size: 0.8rem;
                    line-height: 1.4;
                    font-weight: 500;
                    border-width: 2px;
                    box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.1);
                    transition: all 0.2s ease;
                  }
                  /* Mobile: Compact blocks with organized indicators */
                  @media (max-width: 639px) {
                    .fc-event {
                      padding: 4px 6px;
                      margin: 2px 0;
                      min-height: 24px;
                      display: flex;
                      align-items: center;
                      justify-content: center;
                    }
                    .fc-event-title-container.fc-event-mobile {
                      display: flex;
                      align-items: center;
                      justify-content: center;
                      width: 100%;
                      height: 100%;
                      min-height: 24px;
                    }
                    .fc-event-time-mobile {
                      font-size: 0.75rem;
                      font-weight: 600;
                      text-align: center;
                      width: 100%;
                      opacity: 1;
                      letter-spacing: 0.025em;
                    }
                  }
                  @media (min-width: 1024px) {
                    .fc-event {
                      padding: 4px 6px;
                      margin: 2px 0;
                    }
                  }
                  .fc-event:hover {
                    opacity: 0.9;
                    transform: translateY(-1px);
                    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.15);
                  }
                  .fc-daygrid-event {
                    white-space: normal;
                    word-wrap: break-word;
                    overflow: hidden;
                    text-overflow: ellipsis;
                  }
                  .fc-event-title-container {
                    display: flex;
                    flex-direction: column;
                    gap: 4px;
                    width: 100%;
                  }
                  @media (min-width: 1024px) {
                    .fc-event-title-container {
                      gap: 2px;
                    }
                  }
                  .fc-event-time {
                    font-size: 0.7rem;
                    opacity: 0.95;
                    font-weight: 600;
                    letter-spacing: 0.025em;
                  }
                  @media (min-width: 1024px) {
                    .fc-event-time {
                      font-size: 0.65rem;
                    }
                  }
                  .fc-event-title {
                    font-size: 0.8rem;
                    font-weight: 500;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                  }
                  @media (min-width: 1024px) {
                    .fc-event-title {
                      font-size: 0.75rem;
                    }
                  }
                  .fc-event-badge {
                    display: inline-block;
                    align-self: flex-start;
                    margin-top: 2px;
                  }
                  .fc-col-header-cell {
                    padding: 0.875em;
                    background-color: #f9fafb;
                    border-color: #e5e7eb;
                    font-weight: 600;
                    text-transform: uppercase;
                    font-size: 0.75rem;
                    letter-spacing: 0.05em;
                    color: #6b7280;
                  }
                  .dark .fc-col-header-cell {
                    background-color: #374151;
                    border-color: #4b5563;
                    color: #9ca3af;
                  }
                  @media (min-width: 1024px) {
                    .fc-col-header-cell {
                      padding: 0.5em 0.6em;
                      font-size: 0.7rem;
                    }
                  }
                  .fc-daygrid-day-frame {
                    min-height: 100px;
                  }
                  @media (min-width: 1024px) {
                    .fc-daygrid-day-frame {
                      min-height: 75px;
                    }
                  }
                  .fc-daygrid-event-dot {
                    display: none;
                  }
                  .fc-scrollgrid {
                    border-color: #e5e7eb;
                  }
                  .dark .fc-scrollgrid {
                    border-color: #4b5563;
                  }
                  .fc-scrollgrid-section > td {
                    border-color: #e5e7eb;
                  }
                  .dark .fc-scrollgrid-section > td {
                    border-color: #4b5563;
                  }
                  .fc-daygrid-event.fc-event-past {
                    opacity: 0.7;
                  }
                  .fc-view-harness {
                    padding-bottom: 0.5em;
                  }
                  @media (min-width: 1024px) {
                    .fc-view-harness {
                      padding-bottom: 0;
                    }
                  }
                  .fc-scrollgrid-sync-table {
                    margin-bottom: 0;
                  }
                  .fc-scroller {
                    overflow: hidden !important;
                  }
                  .fc-scroller-liquid-absolute {
                    overflow: hidden !important;
                  }
                  @media (min-width: 1024px) {
                    .fc-scrollgrid {
                      overflow: hidden !important;
                    }
                  }
                `}</style>
                <FullCalendar
                  plugins={[dayGridPlugin, interactionPlugin]}
                  initialView="dayGridMonth"
                  events={calendarEvents}
                  headerToolbar={{
                    left: '',
                    center: 'prev title next',
                    right: 'today'
                  }}
                  height={calendarHeight}
                  datesSet={handleDatesSet}
                  eventClick={handleEventClick}
                  dayMaxEvents={dayMaxEventsValue}
                  moreLinkClick="popover"
                  eventDisplay="block"
                  displayEventTime={true}
                  eventTimeFormat={{
                    hour: 'numeric',
                    minute: '2-digit',
                    meridiem: 'short'
                  }}
                  eventContent={(eventInfo) => {
                    const match = eventInfo.event.extendedProps.match;
                    const location = eventInfo.event.extendedProps.location;
                    const homeAway = eventInfo.event.extendedProps.homeAway;
                    const teamName = eventInfo.event.extendedProps.teamName;
                    const userInLineup = eventInfo.event.extendedProps.userInLineup || false;
                    const isPast = isMatchInPast(match.match_date);
                    const availabilityStatus = getAvailabilityStatus(match.id);
                    const availabilityColor = getAvailabilityColor(availabilityStatus);
                    const availabilityText = getAvailabilityText(availabilityStatus);
                    const hasConfirmed = hasUserConfirmedLineup(match.id);
                    // Show warning if user is in lineup but availability doesn't match (not available or not set) and hasn't confirmed
                    // Also show warning if user is in lineup but hasn't confirmed (regardless of availability)
                    const showWarning = userInLineup && !isPast && !hasConfirmed && (
                      availabilityStatus === null || 
                      availabilityStatus === 'unavailable' || 
                      availabilityStatus === 'maybe'
                    );
                    
                    // Detect mobile viewport (threshold: 640px, Tailwind's 'sm' breakpoint)
                    const isMobile = windowWidth < 640;
                    
                    return (
                      <div 
                        title={`${match.opponent_name}${location ? ` @ ${location}` : ''}${homeAway ? ` (${homeAway === 'home' ? 'Home' : 'Away'})` : ''}${teamName ? ` - ${teamName}` : ''}${!isPast ? ` - ${availabilityText}` : ''}${userInLineup ? ' - You\'re in the lineup' : ''}`}
                        className={`fc-event-title-container ${isMobile ? 'fc-event-mobile' : ''}`}
                        style={{ position: 'relative', opacity: isPast ? 0.7 : 1 }}
                      >
                        {/* Mobile: Single unified indicator in top-right corner */}
                        {isMobile ? (
                          <>
                            {/* Show only one indicator - priority: warning > availability */}
                            {showWarning ? (
                              <div
                                style={{
                                  position: 'absolute',
                                  top: '3px',
                                  right: '3px',
                                  width: '14px',
                                  height: '14px',
                                  borderRadius: '50%',
                                  backgroundColor: '#f59e0b',
                                  border: '2px solid rgba(255, 255, 255, 0.95)',
                                  zIndex: 10,
                                  boxShadow: '0 1px 2px rgba(0, 0, 0, 0.3)'
                                }}
                                title="Warning: You're in the lineup but not marked as available. Please contact your captain."
                              />
                            ) : !isPast ? (
                              <div
                                className="fc-availability-indicator"
                                style={{
                                  position: 'absolute',
                                  top: '3px',
                                  right: '3px',
                                  width: '14px',
                                  height: '14px',
                                  borderRadius: '50%',
                                  backgroundColor: availabilityColor,
                                  border: '2px solid rgba(255, 255, 255, 0.95)',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  zIndex: 10,
                                  boxShadow: '0 1px 2px rgba(0, 0, 0, 0.3)'
                                }}
                                title={`Availability: ${availabilityText}`}
                              >
                                {availabilityStatus === 'available' ? (
                                  <CheckCircle className="w-3.5 h-3.5 text-white" />
                                ) : availabilityStatus === 'maybe' ? (
                                  <AlertCircle className="w-3.5 h-3.5 text-white" />
                                ) : availabilityStatus === 'unavailable' ? (
                                  <XCircle className="w-3.5 h-3.5 text-white" />
                                ) : (
                                  <HelpCircle className="w-3.5 h-3.5 text-white" />
                                )}
                              </div>
                            ) : null}
                          </>
                        ) : (
                          <>
                            {/* Desktop: Original layout */}
                            {/* Availability indicator bubble - show for non-past matches */}
                            {!isPast && (
                              <div
                                className="fc-availability-indicator"
                                style={{
                                  position: 'absolute',
                                  top: '2px',
                                  right: '2px',
                                  width: '16px',
                                  height: '16px',
                                  borderRadius: '50%',
                                  backgroundColor: availabilityColor,
                                  border: '1px solid rgba(255, 255, 255, 0.8)',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  zIndex: 10,
                                  boxShadow: '0 1px 2px rgba(0, 0, 0, 0.2)'
                                }}
                                title={`Availability: ${availabilityText}`}
                              >
                                {availabilityStatus === 'available' ? (
                                  <CheckCircle className="w-3 h-3 text-white" />
                                ) : availabilityStatus === 'maybe' ? (
                                  <AlertCircle className="w-3 h-3 text-white" />
                                ) : availabilityStatus === 'unavailable' ? (
                                  <XCircle className="w-3 h-3 text-white" />
                                ) : (
                                  <HelpCircle className="w-3 h-3 text-white" />
                                )}
                              </div>
                            )}
                            {/* Warning indicator - show in bottom right if user is in lineup but not available */}
                            {showWarning && (
                              <div
                                style={{
                                  position: 'absolute',
                                  bottom: '2px',
                                  right: '2px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  zIndex: 10
                                }}
                                title="Warning: You're in the lineup but not marked as available. Please contact your captain."
                              >
                                <svg 
                                  style={{ width: '16px', height: '16px', color: '#f59e0b' }} 
                                  fill="currentColor" 
                                  viewBox="0 0 20 20"
                                >
                                  <path 
                                    fillRule="evenodd" 
                                    d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" 
                                    clipRule="evenodd" 
                                  />
                                </svg>
                              </div>
                            )}
                          </>
                        )}
                        {/* Mobile: Show only colored block with indicators (no text) */}
                        {!isMobile && (
                          <>
                            {/* Desktop: Show time and opponent */}
                            <div className="fc-event-time">{eventInfo.timeText}</div>
                            <div className="fc-event-title" style={{ opacity: isPast ? 0.8 : 1 }}>
                              {homeAway === 'away' ? 'at' : 'vs'} {match.opponent_name}
                            </div>
                          </>
                        )}
                      </div>
                    );
                  }}
                />
                
                {/* Calendar Legend */}
                <div className="mt-0.5">
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 px-2 py-1.5">
                    <div className="flex items-center gap-1.5">
                      <div 
                        className="w-6 h-6 rounded flex-shrink-0"
                        style={{ backgroundColor: '#10b981' }}
                      ></div>
                      <span className="text-xs text-gray-600 dark:text-gray-400">In lineup</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0" />
                      <span className="text-xs text-gray-600 dark:text-gray-400">Available</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <AlertCircle className="w-6 h-6 text-yellow-500 flex-shrink-0" />
                      <span className="text-xs text-gray-600 dark:text-gray-400">If Needed</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <XCircle className="w-6 h-6 text-red-500 flex-shrink-0" />
                      <span className="text-xs text-gray-600 dark:text-gray-400">Unavailable</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <HelpCircle className="w-6 h-6 text-gray-400 dark:text-gray-500 flex-shrink-0" />
                      <span className="text-xs text-gray-600 dark:text-gray-400">Not set</span>
                    </div>
                  </div>
                </div>
              </div>
              )}
              
              {/* Day Matches Modal */}
              {showDayModal && selectedDayMatches.length > 0 && (
                <div 
                  className="fixed inset-0 bg-black bg-opacity-50 flex items-end md:items-center justify-center z-50 p-0 md:p-4"
                  onClick={() => setShowDayModal(false)}
                >
                  <div
                    className="bg-white dark:bg-gray-800 rounded-t-2xl md:rounded-xl shadow-xl max-w-2xl w-full max-h-[85vh] md:max-h-[80vh] overflow-y-auto pt-4 md:pt-6 px-4 md:px-6 pb-4 md:pb-6 relative"
                    onClick={(e) => e.stopPropagation()}
                  >
                      {/* Close Button */}
                      <button
                        onClick={() => setShowDayModal(false)}
                        className="absolute top-3 right-3 md:top-4 md:right-4 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg p-1.5 transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center z-10"
                        aria-label="Close"
                      >
                        <X className="w-5 h-5" />
                      </button>

                      {/* Matches List */}
                      <div className="space-y-6">
                        {selectedDayMatches.map((match) => {
                          const availabilityStatus = getAvailabilityStatus(match.id);
                          const isUpdatingThis = updating === match.id;
                          const isPast = isMatchInPast(match.match_date);
                          const userInLineup = isUserInLineup(match.id);
                          const hasConfirmed = hasUserConfirmedLineup(match.id);
                    // Don't show warning if user has confirmed their lineup position
                    const showWarning = userInLineup && !isPast && availabilityStatus !== 'available' && !hasConfirmed;
                          
                          return (
                            <div key={match.id}>
                              {/* Match Header */}
                              <div className="px-0 py-4">
                                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                                  <div className="flex-1 min-w-0">
                                    <h4 className="text-lg md:text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
                                      {match.home_away === 'away' ? 'at' : 'vs'} {match.opponent_name}
                                    </h4>
                                    {(showWarning || (userInLineup && !showWarning)) && !isPast && (
                                      <div className="mb-2">
                                        {showWarning && (
                                          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300 border border-yellow-300 dark:border-yellow-700">
                                            <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                            </svg>
                                            You're in the lineup, but not available
                                          </span>
                                        )}
                                        {userInLineup && !showWarning && (
                                          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 border border-green-300 dark:border-green-700">
                                            <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                            </svg>
                                            You're in the lineup
                                          </span>
                                        )}
                                      </div>
                                    )}
                                    {match.team && (
                                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-2 flex items-center gap-2">
                                        <Users className="w-4 h-4 flex-shrink-0" />
                                        {match.team.name}
                                      </p>
                                    )}
                                    <div className="space-y-1">
                                      {(() => {
                                        const formatted = formatMatchDateFull(match.match_date);
                                        return (
                                          <>
                                            <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                                              <Calendar className="w-4 h-4 flex-shrink-0" />
                                              <span>{formatted.date}</span>
                                            </div>
                                            <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                                              <Clock className="w-4 h-4 flex-shrink-0" />
                                              <span>{formatted.time}</span>
                                            </div>
                                          </>
                                        );
                                      })()}
                                    </div>
                                    <div className="flex flex-wrap items-center gap-3 text-sm text-gray-600 dark:text-gray-400">
                                      {(match.location || match.formatted_address) && (
                                        <div className="flex items-center gap-1.5">
                                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                          </svg>
                                          <span>
                                            {match.location && match.formatted_address
                                              ? `${match.location} - ${match.formatted_address}`
                                              : match.formatted_address || match.location}
                                          </span>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                  {/* Mobile: Action menu */}
                                  <div className="sm:hidden">
                                    <ActionMenu
                                      actions={[
                                        {
                                          label: 'View Details',
                                          onClick: () => {
                                            setShowDayModal(false);
                                            navigate(`/matches/${match.id}`);
                                          },
                                          variant: 'primary',
                                          icon: null
                                        }
                                      ]}
                                    />
                                  </div>
                                </div>
                              </div>
                              
                              {/* Warning Banner - below match details for context */}
                              {showWarning && (
                                <div className="bg-yellow-50 dark:bg-yellow-900/20 border-l-4 border-yellow-400 dark:border-yellow-600 p-4 rounded-md mb-4">
                                  <div className="flex items-start">
                                    <div className="flex-shrink-0">
                                      <svg className="h-5 w-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                                        <path
                                          fillRule="evenodd"
                                          d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                                          clipRule="evenodd"
                                        />
                                      </svg>
                                    </div>
                                    <div className="ml-3">
                                      <h3 className="text-sm font-medium text-yellow-800 dark:text-yellow-300">
                                        You're in the lineup but not marked as available
                                      </h3>
                                      <p className="mt-1 text-sm text-yellow-700 dark:text-yellow-400">
                                        Please contact your captain to update your availability or lineup status.
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              )}

                              {/* Match Content */}
                              <div className="space-y-6">
                                {/* Notes */}
                                {match.notes && (
                                  <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md p-3">
                                    <p className="text-sm text-blue-900 dark:text-blue-200">{match.notes}</p>
                                  </div>
                                )}
                                
                                {/* Availability Section - Only show for non-past matches */}
                                {!isPast && (
                                  <div className="space-y-3">
                                    <div className="flex items-center justify-between">
                                      <h5 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">Your Availability</h5>
                                      {availabilityStatus === null && (
                                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${getStatusBadgeClass(availabilityStatus)}`}>
                                          {getAvailabilityText(availabilityStatus)}
                                        </span>
                                      )}
                                    </div>
                                    
                                    {/* Availability Action Buttons */}
                                    <div className="grid grid-cols-3 gap-3">
                                      {(() => {
                                        const isSelected = availabilityStatus === 'available';
                                        const hasSelection = availabilityStatus !== null;
                                        return (
                                          <button
                                            onClick={() => handleAvailabilityChange(match.id, 'available')}
                                            disabled={isUpdatingThis}
                                            className={`px-4 py-3 rounded-lg text-sm font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px] flex items-center justify-center gap-2 ${
                                              isSelected
                                                ? 'bg-green-600 text-white shadow-md ring-2 ring-green-300'
                                                : hasSelection
                                                ? 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 border border-gray-200 dark:border-gray-600 opacity-50'
                                                : 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300 hover:bg-green-100 dark:hover:bg-green-900/50 border border-green-200 dark:border-green-800'
                                            }`}
                                          >
                                            <CheckCircle className={`w-4 h-4 ${isSelected ? 'text-white' : hasSelection ? 'text-gray-400 dark:text-gray-500' : 'text-green-600 dark:text-green-500'}`} />
                                            {isUpdatingThis ? 'Updating...' : 'Available'}
                                          </button>
                                        );
                                      })()}
                                      {(() => {
                                        const isSelected = availabilityStatus === 'maybe';
                                        const hasSelection = availabilityStatus !== null;
                                        return (
                                          <button
                                            onClick={() => handleAvailabilityChange(match.id, 'maybe')}
                                            disabled={isUpdatingThis}
                                            className={`px-4 py-3 rounded-lg text-sm font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px] flex items-center justify-center gap-2 ${
                                              isSelected
                                                ? 'bg-yellow-600 text-white shadow-md ring-2 ring-yellow-300'
                                                : hasSelection
                                                ? 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 border border-gray-200 dark:border-gray-600 opacity-50'
                                                : 'bg-yellow-50 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300 hover:bg-yellow-100 dark:hover:bg-yellow-900/50 border border-yellow-200 dark:border-yellow-800'
                                            }`}
                                          >
                                            <AlertCircle className={`w-4 h-4 ${isSelected ? 'text-white' : hasSelection ? 'text-gray-400 dark:text-gray-500' : 'text-yellow-600 dark:text-yellow-500'}`} />
                                            {isUpdatingThis ? 'Updating...' : 'If Needed'}
                                          </button>
                                        );
                                      })()}
                                      {(() => {
                                        const isSelected = availabilityStatus === 'unavailable';
                                        const hasSelection = availabilityStatus !== null;
                                        return (
                                          <button
                                            onClick={() => handleAvailabilityChange(match.id, 'unavailable')}
                                            disabled={isUpdatingThis}
                                            className={`px-4 py-3 rounded-lg text-sm font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px] flex items-center justify-center gap-2 ${
                                              isSelected
                                                ? 'bg-red-600 text-white shadow-md ring-2 ring-red-300'
                                                : hasSelection
                                                ? 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 border border-gray-200 dark:border-gray-600 opacity-50'
                                                : 'bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 hover:bg-red-100 dark:hover:bg-red-900/50 border border-red-200 dark:border-red-800'
                                            }`}
                                          >
                                            <XCircle className={`w-4 h-4 ${isSelected ? 'text-white' : hasSelection ? 'text-gray-400 dark:text-gray-500' : 'text-red-600 dark:text-red-500'}`} />
                                            {isUpdatingThis ? 'Updating...' : 'Unavailable'}
                                          </button>
                                        );
                                      })()}
                                    </div>
                                  </div>
                                )}
                                
                                {/* Match Lineup */}
                                {(() => {
                                  const lineup = modalLineups[match.id] || [];
                                  const isPast = isMatchInPast(match.match_date);
                                  const isPublished = match.lineup_published === true;
                                  
                                  // Only show lineup section if it's published or the match is in the past
                                  if (!isPast && !isPublished) {
                                    return null;
                                  }
                                  
                                  return (
                                    <div className="space-y-3 pt-4 border-t border-gray-100 dark:border-gray-700/50">
                                      <h5 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">Match Lineup</h5>
                                      {lineup.length === 0 ? (
                                        <div className="text-center py-6">
                                          <svg className="mx-auto h-10 w-10 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                                          </svg>
                                          <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">No lineup set</h3>
                                          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                                            The lineup for this match has not been set yet.
                                          </p>
                                        </div>
                                      ) : (
                                        <div className="space-y-2">
                                        {lineup.map((position) => {
                                          // Get match type from team's lineup format
                                          const lineupFormat = teamLineupFormats[match.team_id];
                                          const formatPosition = lineupFormat?.positions?.find(
                                            (fp: any) => fp.position_number === position.position_number
                                          );
                                          const matchType = formatPosition?.match_type || '';
                                          const isSingles = matchType ? isSinglesPosition(matchType) : !position.player2_id;
                                          
                                          // Count how many positions have the same match type
                                          const matchTypeCount = lineup.filter(p => {
                                            const fp = lineupFormat?.positions?.find(
                                              (fpos: any) => fpos.position_number === p.position_number
                                            );
                                            return fp?.match_type === matchType;
                                          }).length;
                                          
                                          // Determine the index within positions of the same match type
                                          const sameTypePositions = lineup.filter(p => {
                                            const fp = lineupFormat?.positions?.find(
                                              (fpos: any) => fpos.position_number === p.position_number
                                            );
                                            return fp?.match_type === matchType;
                                          }).sort((a, b) => a.position_number - b.position_number);
                                          const positionIndexWithinType = sameTypePositions.findIndex(
                                            p => p.position_number === position.position_number
                                          ) + 1;
                                          
                                          // Create the display label
                                          const formattedType = matchType ? formatMatchType(matchType) : '';
                                          const displayLabel = matchType && matchTypeCount > 1
                                            ? `${formattedType} ${positionIndexWithinType}`
                                            : formattedType || `Line ${position.position_number}`;
                                          
                                          const hasResult = position.score || position.won === true || position.won === false;
                                          const isWon = position.won === true;
                                          const isLost = position.won === false;

                                          // Check if opponents are set
                                          const hasOpponents = position.opponent_player1_name || position.opponent_player2_name;

                                          // Check if user is in this position
                                          const userInPosition = user && (position.player1_id === user.id || position.player2_id === user.id);
                                          // Check if user has confirmed this position
                                          const userConfirmedPosition = userInPosition && isPlayerConfirmed(position, user.id);
                                          // Check if warning condition applies (user in position + warning state)
                                          const positionWarning = userInPosition && showWarning && !isPast;
                                          // Determine border classes - animate if in lineup but not confirmed (even in warning state)
                                          const borderClasses = positionWarning
                                            ? userConfirmedPosition
                                              ? 'ring-2 ring-yellow-400 border-yellow-500 dark:border-yellow-500'
                                              : 'animate-pulse-border-yellow ring-2 ring-yellow-400 border-yellow-500 dark:border-yellow-500'
                                            : userInPosition
                                            ? userConfirmedPosition
                                              ? 'ring-2 ring-green-400 border-green-600 dark:border-green-600'
                                              : 'animate-pulse-border-green ring-2 ring-green-400 border-green-600 dark:border-green-600'
                                            : 'border border-gray-200 dark:border-gray-700';

                                          return (
                                            <div key={position.id} className={`${borderClasses} rounded-lg p-2.5 bg-gray-50 dark:bg-gray-700/50 group transition-all`}>
                                              <div className="flex items-center justify-between mb-2">
                                                <h6 className="text-sm font-medium text-gray-900 dark:text-gray-100 inline-flex items-center gap-1.5">
                                                  {matchType && <MatchTypeIcon matchType={matchType} iconSize="w-4 h-4" />}
                                                  {displayLabel}
                                                </h6>
                                                {hasResult && position.score && (
                                                  <span className="inline-flex items-center gap-2">
                                                    <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                                                      {position.score}
                                                    </span>
                                                    {position.difficulty && (
                                                      <DifficultyGauge
                                                        teamAvg={position.difficulty.team_avg_rating}
                                                        opponentAvg={position.difficulty.opponent_avg_rating}
                                                      />
                                                    )}
                                                  </span>
                                                )}
                                              </div>
                                              <div className={`grid ${hasOpponents ? 'grid-cols-2' : 'grid-cols-1'} gap-4 text-sm`}>
                                                {/* Left side - Our players */}
                                                <div className="flex flex-col">
                                                  {isWon && (
                                                    <div className="mb-1.5">
                                                      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 border border-green-300 dark:border-green-700">
                                                        Won
                                                      </span>
                                                    </div>
                                                  )}
                                                  {!isWon && hasResult && (
                                                    <div className="h-[28px]"></div>
                                                  )}
                                                  <div className="space-y-1.5">
                                                    <div className="flex items-center gap-1.5">
                                                      <span className="text-xs text-gray-500 dark:text-gray-400 mr-1">
                                                        {isSingles ? 'Player:' : 'Player 1:'}
                                                      </span>
                                                      {position.player1?.name ? (
                                                        <div className="flex items-center gap-2">
                                                          <span className="font-medium text-gray-900 dark:text-gray-100">
                                                            {getDisplayName(position.player1)}
                                                          </span>
                                                            {match.lineup_published && !isMatchInPast(match.match_date) && position.player1_id && (
                                                              <div className="flex items-center gap-1.5 ml-1">
                                                                {getConfirmationStatus(position, position.player1_id)}
                                                                {canConfirmPositionInModal(position.player1_id, match) && (
                                                                  isPlayerConfirmed(position, position.player1_id) ? (
                                                                    <button
                                                                      onClick={() => handleUnconfirmInModal(match.id, position.id, position.player1_id)}
                                                                      className="opacity-100 md:opacity-0 md:group-hover:opacity-100 px-2 py-0.5 text-xs font-medium text-gray-600 dark:text-gray-400 rounded hover:bg-gray-50 dark:hover:bg-gray-900/20 transition-all"
                                                                      title="Unconfirm"
                                                                    >
                                                                      Unconfirm
                                                                    </button>
                                                                  ) : (
                                                                    <button
                                                                      onClick={() => handleConfirmInModal(match.id, position.id, position.player1_id)}
                                                                      className="opacity-100 px-2 py-0.5 text-xs font-medium text-gray-600 dark:text-gray-400 rounded hover:bg-gray-50 dark:hover:bg-gray-900/20 transition-all"
                                                                      title="Confirm"
                                                                    >
                                                                      Confirm
                                                                    </button>
                                                                  )
                                                                )}
                                                              </div>
                                                            )}
                                                        </div>
                                                      ) : (
                                                        <span className="font-medium text-gray-400 dark:text-gray-500 italic">
                                                          TBD
                                                        </span>
                                                      )}
                                                    </div>
                                                    {!isSingles && (
                                                      <div className="flex items-center gap-1.5">
                                                        <span className="text-xs text-gray-500 dark:text-gray-400 mr-1">Player 2:</span>
                                                        {position.player2?.name ? (
                                                          <div className="flex items-center gap-2">
                                                            <span className="font-medium text-gray-900 dark:text-gray-100">
                                                              {getDisplayName(position.player2)}
                                                            </span>
                                                            {match.lineup_published && !isMatchInPast(match.match_date) && position.player2_id && (() => {
                                                              const player2Id = position.player2_id!; // Already checked above
                                                              return (
                                                                <div className="flex items-center gap-1.5 ml-1">
                                                                  {getConfirmationStatus(position, player2Id)}
                                                                  {canConfirmPositionInModal(player2Id, match) && (
                                                                    isPlayerConfirmed(position, player2Id) ? (
                                                                      <button
                                                                        onClick={() => handleUnconfirmInModal(match.id, position.id, player2Id)}
                                                                        className="opacity-100 md:opacity-0 md:group-hover:opacity-100 px-2 py-0.5 text-xs font-medium text-gray-600 dark:text-gray-400 rounded hover:bg-gray-50 dark:hover:bg-gray-900/20 transition-all"
                                                                        title="Unconfirm"
                                                                      >
                                                                        Unconfirm
                                                                      </button>
                                                                    ) : (
                                                                      <button
                                                                        onClick={() => handleConfirmInModal(match.id, position.id, player2Id)}
                                                                        className="opacity-100 px-2 py-0.5 text-xs font-medium text-gray-600 dark:text-gray-400 rounded hover:bg-gray-50 dark:hover:bg-gray-900/20 transition-all"
                                                                        title="Confirm"
                                                                      >
                                                                        Confirm
                                                                      </button>
                                                                    )
                                                                  )}
                                                                </div>
                                                              );
                                                            })()}
                                                          </div>
                                                        ) : (
                                                          <span className="font-medium text-gray-400 dark:text-gray-500 italic">
                                                            TBD
                                                          </span>
                                                        )}
                                                      </div>
                                                    )}
                                                  </div>
                                                </div>
                                                {/* Right side - Opponents (only show if opponents are set) */}
                                                {hasOpponents && (
                                                  <div className="flex flex-col">
                                                    {isLost && (
                                                      <div className="mb-1.5">
                                                        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 border border-green-300 dark:border-green-700">
                                                          Won
                                                        </span>
                                                      </div>
                                                    )}
                                                    {!isLost && hasResult && (
                                                      <div className="h-[28px]"></div>
                                                    )}
                                                    <div className="space-y-1.5">
                                                      {isSingles ? (
                                                        position.opponent_player1_name && (
                                                          <div>
                                                            <span className="text-xs text-gray-500 dark:text-gray-400 mr-1">Opponent:</span>
                                                            <span className="font-medium text-gray-900 dark:text-gray-100">
                                                              {position.opponent_player1_name}
                                                            </span>
                                                          </div>
                                                        )
                                                      ) : (
                                                        <>
                                                          {position.opponent_player1_name && (
                                                            <div>
                                                              <span className="text-xs text-gray-500 dark:text-gray-400 mr-1">Opponent 1:</span>
                                                              <span className="font-medium text-gray-900 dark:text-gray-100">
                                                                {position.opponent_player1_name}
                                                              </span>
                                                            </div>
                                                          )}
                                                          {position.opponent_player2_name && (
                                                            <div>
                                                              <span className="text-xs text-gray-500 dark:text-gray-400 mr-1">Opponent 2:</span>
                                                              <span className="font-medium text-gray-900 dark:text-gray-100">
                                                                {position.opponent_player2_name}
                                                              </span>
                                                            </div>
                                                          )}
                                                        </>
                                                      )}
                                                    </div>
                                                  </div>
                                                )}
                                              </div>
                                            </div>
                                          );
                                        })}
                                        </div>
                                      )}
                                    </div>
                                  );
                                })()}
                                
                                {/* Action Buttons */}
                                <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-gray-100 dark:border-gray-700/50">
                                  <Link
                                    to={`/matches/${match.id}`}
                                    onClick={() => setShowDayModal(false)}
                                    className="flex-1 inline-flex items-center justify-center gap-2 font-medium rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 focus:ring-gray-500 px-4 py-3 text-sm min-h-[44px]"
                                  >
                                    <Eye className="w-4 h-4" />
                                    View Details
                                  </Link>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* List View */}
          {viewMode === 'list' && (
            <>
              {loading ? (
                <div className="space-y-4">
                  {[...Array(5)].map((_, i) => (
                    <MatchCardSkeleton key={i} />
                  ))}
                </div>
              ) : filteredMatches.length === 0 ? (
                <div className="bg-white dark:bg-gray-800 rounded-xl p-8 md:p-10 text-center shadow-sm border border-gray-100 dark:border-gray-700/50">
                  <div className="w-12 h-12 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center mx-auto mb-4">
                    <CalendarIcon className="w-6 h-6 text-gray-400 dark:text-gray-500" />
                  </div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {matchFilter === 'upcoming' && 'No upcoming matches'}
                    {matchFilter === 'past' && 'No past matches'}
                    {matchFilter === 'all' && 'No matches found'}
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {filteredMatches.map((match) => (
                    <MatchCard
                      key={match.id}
                      match={match}
                      user={user}
                      matchLineups={matchLineups}
                      availability={availability}
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Confirm Availability Modal */}
      <ConfirmAvailabilityModal
        isOpen={showConfirmModal}
        onConfirm={handleModalConfirm}
        onCancel={handleModalCancel}
      />
    </div>
  );
};

export default MatchesPage;
